import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

// Brand palette (mirrors Admin.css)
const BRAND = [154, 106, 255];   // #9a6aff  purple
const GREEN = [16, 122, 87];     // delivered text
const RED = [192, 42, 14];       // failed text
const MUTED = [107, 114, 128];   // grey

const GREEN_BG = [223, 246, 236];
const RED_BG = [253, 232, 226];

const statusCellHook = (data) => {
  // Colour the "Status" column based on its value (Delivered / Failed).
  if (data.section !== 'body' || data.column.dataKey !== 'status') return;
  const value = String(data.cell.raw || '').toLowerCase();
  if (value === 'delivered') {
    data.cell.styles.textColor = GREEN;
    data.cell.styles.fillColor = GREEN_BG;
    data.cell.styles.fontStyle = 'bold';
  } else if (value === 'failed') {
    data.cell.styles.textColor = RED;
    data.cell.styles.fillColor = RED_BG;
    data.cell.styles.fontStyle = 'bold';
  }
};

const sectionTitle = (doc, title, y) => {
  doc.setFontSize(13);
  doc.setTextColor(...BRAND);
  doc.setFont(undefined, 'bold');
  doc.text(title, 40, y);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(0, 0, 0);
};

const drawTable = (doc, { title, startY, columns, rows, emptyText }) => {
  // Start a fresh page if there isn't enough room for the section header.
  const pageHeight = doc.internal.pageSize.getHeight();
  let y = startY;
  if (y > pageHeight - 90) {
    doc.addPage();
    y = 50;
  }

  sectionTitle(doc, title, y);

  if (!rows.length) {
    doc.setFontSize(10);
    doc.setTextColor(...MUTED);
    doc.setFont(undefined, 'italic');
    doc.text(emptyText, 40, y + 18);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(0, 0, 0);
    return y + 26;
  }

  autoTable(doc, {
    startY: y + 8,
    columns,
    body: rows,
    margin: { left: 40, right: 40 },
    styles: { fontSize: 9, cellPadding: 5, overflow: 'linebreak', valign: 'middle' },
    headStyles: { fillColor: BRAND, textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [248, 247, 255] },
    didParseCell: statusCellHook,
  });
  return doc.lastAutoTable.finalY;
};

/**
 * Build a Flashback delivery report PDF entirely in the browser and trigger an
 * automatic download. Nothing is stored server-side — `report` is the per-run
 * object returned by the /flashback-progress endpoint.
 *
 * report = { eventName, sms: [...], email: [...], incomplete: [...] }
 */
export function generateFlashbackReport(report) {
  const sms = report?.sms || [];
  const email = report?.email || [];
  const incomplete = report?.incomplete || [];
  const eventName = report?.eventName || 'Event';
  const generatedAt = new Date().toLocaleString();

  const countBy = (rows, status) =>
    rows.filter((r) => String(r.status).toLowerCase() === status).length;

  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();

  // ---- Header ----
  doc.setFillColor(...BRAND);
  doc.rect(0, 0, pageWidth, 70, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(20);
  doc.setFont(undefined, 'bold');
  doc.text('ImageShield Delivery Report', 40, 38);
  doc.setFontSize(10);
  doc.setFont(undefined, 'normal');
  doc.text(`Event: ${eventName}`, 40, 56);
  doc.text(`Generated: ${generatedAt}`, pageWidth - 40, 56, { align: 'right' });

  // ---- Summary ----
  doc.setTextColor(...MUTED);
  doc.setFontSize(10);
  const summary =
    `SMS: ${sms.length} attempted  (${countBy(sms, 'delivered')} delivered, ${countBy(sms, 'failed')} failed)   |   ` +
    `Email: ${email.length} attempted  (${countBy(email, 'delivered')} delivered, ${countBy(email, 'failed')} failed)   |   ` +
    `Incomplete profiles: ${incomplete.length}`;
  doc.text(summary, 40, 92);

  let y = 120;

  // ---- Table 1: SMS ----
  y = drawTable(doc, {
    title: '1.  Photos sent by SMS',
    startY: y,
    columns: [
      { header: '#', dataKey: 'idx' },
      { header: 'Name', dataKey: 'name' },
      { header: 'Phone Number', dataKey: 'phone' },
      { header: 'Status', dataKey: 'status' },
      { header: 'Details', dataKey: 'reason' },
    ],
    rows: sms.map((r, i) => ({
      idx: i + 1,
      name: r.name || '-',
      phone: r.phone || '-',
      status: r.status,
      reason: r.reason || '-',
    })),
    emptyText: 'No SMS were sent in this run.',
  });

  // ---- Table 2: Email ----
  y = drawTable(doc, {
    title: '2.  Photos sent by Email',
    startY: y + 30,
    columns: [
      { header: '#', dataKey: 'idx' },
      { header: 'Name', dataKey: 'name' },
      { header: 'Email', dataKey: 'email' },
      { header: 'Status', dataKey: 'status' },
      { header: 'Details', dataKey: 'reason' },
    ],
    rows: email.map((r, i) => ({
      idx: i + 1,
      name: r.name || '-',
      email: r.email || '-',
      status: r.status,
      reason: r.reason || '-',
    })),
    emptyText: 'No emails were sent in this run.',
  });

  // ---- Table 3: Incomplete profiles ----
  drawTable(doc, {
    title: '3.  Incomplete profiles',
    startY: y + 30,
    columns: [
      { header: '#', dataKey: 'idx' },
      { header: 'Name', dataKey: 'name' },
      { header: 'Contact / ID', dataKey: 'contact' },
      { header: 'Missing', dataKey: 'missing' },
    ],
    rows: incomplete.map((r, i) => ({
      idx: i + 1,
      name: r.name || '-',
      contact: r.contact || '-',
      missing: r.missing || '-',
    })),
    emptyText: 'All detected profiles are complete.',
  });

  const safeEvent = String(eventName).replace(/[^a-z0-9_-]+/gi, '_');
  const stamp = new Date().toISOString().slice(0, 10);
  const fileName = `ImageShield_Report_${safeEvent}_${stamp}.pdf`;

  // Open the report in a new tab (desktop) / the device's PDF viewer (mobile), AND download it.
  const blob = doc.output('blob');
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');

  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Revoke later so the opened tab has time to load the blob.
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}
